<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS PRINCIPALES -->
        <div class="param-title-wrap niif-header" v-if="showFilters">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        v-model="selReporte"
                        @change="handleChangeReporte"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in listReportes" :key="reporte.codigo" :value="reporte.codigo">
                            {{ reporte?.codigo }} - {{ reporte?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="selVigencia"
                        @change="handleChangeVigencia"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencias" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="grupoId"
                        @change="filtrarCompaniesPorGrupo"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="todos">
                            {{ labels.allGroups }}
                        </b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="companyId"
                        @change="handleChangeCompany"
                        class="filter-select ml-2"
                        :disabled="filteredCompanies.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option v-for="company in filteredCompanies" :key="company.id" :value="String(company.id)">
                            {{ company?.acronym ? company?.acronym + ' ' : '' }}{{ company?.nombre }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultarFilas"
                        :disabled="!isFormValid || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>

                    <b-button
                        variant="link"
                        class="toggle-filters-btn ml-2"
                        @click="toggleFilters"
                        v-if="showFilas"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-up']" class="mr-1" />
                        {{ labels.hideFilters }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- SECCIÓN DE FILAS CONFIGURADAS -->
        <div class="admin-table-block mt-4" v-if="showFilas && !isLoading">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">
                    <b><i>{{ labels.rows }}</i></b>
                    <span class="text-muted ml-2" style="font-size: 14px; font-weight: normal;">
                        ({{ listFilasPrincipales.length }} {{ labels.rowsConfigured }})
                    </span>
                </h5>
                <b-button
                    size="sm"
                    class="table-toolbar-add-btn"
                    @click="abrirModalCrearFila"
                    :disabled="isVigenciaBloqueada || !consultado"
                >
                    <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                    {{ labels.addRow }}
                </b-button>
            </div>

            <div class="rows-list-container" style="max-height: 400px; overflow-y: auto;">
                <div v-if="listFilasPrincipales?.length === 0" class="text-center py-4">
                    <font-awesome-icon :icon="['fas', 'list']" class="empty-table-icon" />
                    <p class="text-muted mt-2">{{ labels.notRows }}</p>
                </div>
                <div v-else>
                    <div 
                        class="fila-item d-flex justify-content-between align-items-center p-3 mb-2 border rounded"
                        v-for="fila in listFilasPrincipales" 
                        :key="fila.id" 
                        @click="seleccionarFila(fila)"
                        :class="{ 'selected-item': filaSeleccionada && filaSeleccionada.id === fila.id }"
                        style="cursor: pointer; transition: all 0.2s ease;"
                    >
                        <div class="d-flex align-items-center">
                            <span class="badge badge-primary mr-3" style="font-size: 14px; padding: 6px 12px;">
                                {{ fila?.orden }}
                            </span>
                            <span class="font-weight-medium">{{ fila?.descripcion || '-' }}</span>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <b-button
                                style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px;"
                                class="md-fab md-primary"
                                @click.stop="editarFila(fila)"
                                :disabled="isVigenciaBloqueada"
                                v-b-tooltip.hover :title="labels.edit"
                            >
                                <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                            </b-button>
                            <b-button
                                class="reset-btn-danger"
                                style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px;"
                                @click.stop="eliminarFila(fila)"
                                :disabled="isVigenciaBloqueada"
                                v-b-tooltip.hover :title="labels.delete"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- LOADING SPINNER -->
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
            <p class="mt-3 text-primary"><b><i>{{ labels.loading }}</i></b></p>
        </div>

        <!-- SECCIÓN DE DETALLE - FORMULACIONES -->
        <div class="admin-table-block mt-4" v-if="filaSeleccionada && !isLoading">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">
                    <b><i>{{ labels.formulationsFor }}</i></b>
                    <span class="text-primary">{{ filaSeleccionada?.orden }} - {{ filaSeleccionada?.descripcion }}</span>
                    <span class="text-muted ml-2" style="font-size: 14px; font-weight: normal;">
                        ({{ listFormulaciones.length }} {{ labels.formulations }})
                    </span>
                </h5>
                <b-button
                    size="sm"
                    class="table-toolbar-add-btn"
                    @click="abrirModalCrearFormulacion"
                    :disabled="isVigenciaBloqueada || !filaSeleccionada"
                >
                    <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                    {{ labels.addFormulation }}
                </b-button>
            </div>

            <div v-if="listFormulaciones?.length === 0" class="text-center py-4">
                <font-awesome-icon :icon="['fas', 'file-alt']" class="empty-table-icon" />
                <p class="text-muted mt-2">{{ labels.noFormulations }}</p>
            </div>

            <CorporateDataTable
                v-else
                :items="formulacionesTableData"
                :fields="formulacionesFields"
                :labels="formulacionesTableLabels"
                :default-per-page="10"
                :page-options="[10, 20, 50, 100]"
                :narrow="false"
                :table-min-height="300"
                :table-max-height="400"
                :show-search="true"
                :allow-column-toggle="true"
                :enable-sorting="true"
                :filter-defs="formulacionesFilterDefs"
                :toolbar-disabled="false"
            >
                <template #cell(accionEdit)="{ item }">
                    <b-button
                        style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                        class="md-fab md-primary"
                        @click.stop="editarFormulacion(item)"
                        :disabled="isVigenciaBloqueada"
                    >
                        <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                    </b-button>
                </template>

                <template #cell(accionDelete)="{ item }">
                    <b-button
                        class="reset-btn-danger"
                        style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                        @click.stop="eliminarFormulacion(item)"
                        :disabled="isVigenciaBloqueada"
                    >
                        <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                    </b-button>
                </template>

                <template #empty>
                    <div class="text-center py-4">
                        <font-awesome-icon :icon="['fas', 'file-alt']" class="empty-table-icon" />
                        <p class="text-muted mt-2">{{ labels.noFormulations }}</p>
                    </div>
                </template>
            </CorporateDataTable>
        </div>

        <!-- MODAL CREAR/EDITAR FILA -->
        <b-modal
            :model-value="showModalFila"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="md"
            :title="isEditingFila ? labels.editRow : labels.createRow"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label required">{{ labels.order }}</label>
                    </div>
                    <div class="col-md-8">
                        <input 
                            v-model="filaFormData.orden" 
                            type="number" 
                            min="1"
                            class="form-control form-control-sm form-input"
                            :class="{ 'is-invalid': !filaFormData.orden && touchedFila }"
                            @blur="touchedFila = true"
                        />
                        <div v-if="!filaFormData.orden && touchedFila" class="invalid-feedback">
                            {{ labels.requiredField }}
                        </div>
                    </div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label">{{ labels.description }}</label>
                    </div>
                    <div class="col-md-8">
                        <input 
                            v-model="filaFormData.descripcion" 
                            maxlength="100"
                            class="form-control form-control-sm form-input"
                            placeholder="Ingrese una descripción..."
                        />
                    </div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="cerrarModalFila">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button 
                        class="reset-btn-primary ml-2" 
                        @click="guardarFila" 
                        :disabled="!filaFormData.orden || isVigenciaBloqueada || isLoading"
                    >
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CREAR/EDITAR FORMULACIÓN -->
        <b-modal
            :model-value="showModalFormulacion"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="xl"
            :title="isEditingFormulacion ? labels.editFormulation : labels.createFormulation"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <!-- Campos básicos -->
                <div class="row">
                    <div class="col-md-4">
                        <div class="uniform-form-row mb-3">
                            <label class="field-label required">{{ labels.accounting }}</label>
                            <b-form-select 
                                v-model="formulacionData.contable" 
                                @change="cambiarTipoFormulacion"
                                class="form-control-sm form-input"
                            >
                                <option value="1">{{ labels.yes }}</option>
                                <option value="0">{{ labels.no }}</option>
                            </b-form-select>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="uniform-form-row mb-3">
                            <label class="field-label required">{{ labels.current }}</label>
                            <b-form-select 
                                v-model="formulacionData.actual"
                                class="form-control-sm form-input"
                            >
                                <option value="1">{{ labels.yes }}</option>
                                <option value="0">{{ labels.no }}</option>
                            </b-form-select>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="uniform-form-row mb-3">
                            <label class="field-label required">{{ labels.operation }}</label>
                            <b-form-select 
                                v-model="formulacionData.operacion"
                                class="form-control-sm form-input"
                            >
                                <option value="+">{{ labels.add }}</option>
                                <option value="-">{{ labels.subtract }}</option>
                            </b-form-select>
                        </div>
                    </div>
                </div>

                <!-- Selección de Cuentas (Contable = 1) -->
                <div v-if="formulacionData?.contable === '1'">
                    <div class="form-group mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="field-label">{{ labels.selectAccounts }}</label>
                            <span class="badge badge-primary">
                                {{ labels.selected }}: {{ formulacionData.cuentasSeleccionadas?.length || 0 }}
                            </span>
                        </div>
                        <div class="search-bar mb-2">
                            <input 
                                v-model="cuentasSearchTerm" 
                                :placeholder="labels.searchAccounts"
                                class="form-control form-control-sm"
                                @input="filtrarCuentas"
                            />
                        </div>
                        <div class="table-container" style="max-height: 300px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px;">
                            <table class="table table-sm table-hover mb-0">
                                <thead style="position: sticky; top: 0; background: white; z-index: 1;">
                                    <tr>
                                        <th style="width: 40px; text-align: center;">
                                            <input 
                                                type="checkbox" 
                                                :checked="selectAllCuentas"
                                                @change="toggleSelectAllCuentas"
                                            />
                                        </th>
                                        <th style="width: 120px;">Cuenta</th>
                                        <th>Descripción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="cuenta in filteredCuentas" :key="cuenta.cuenta">
                                        <td style="text-align: center;">
                                            <input 
                                                type="checkbox" 
                                                v-model="cuenta._selected"
                                                @change="onCuentaCheckboxChange(cuenta)"
                                            />
                                        </td>
                                        <td>{{ cuenta?.cuenta }}</td>
                                        <td :title="cuenta?.descripcion">{{ truncarTexto(cuenta?.descripcion, 60) }}</td>
                                    </tr>
                                    <tr v-if="filteredCuentas?.length === 0">
                                        <td colspan="3" class="text-center text-muted py-3">
                                            {{ labels.noAccounts }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Formulación (Contable = 0) -->
                <div v-if="formulacionData?.contable === '0'">
                    <div class="row">
                        <div class="col-md-4">
                            <div class="uniform-form-row mb-3">
                                <label class="field-label required">{{ labels.formulationType }}</label>
                                <b-form-select 
                                    v-model="formulacionData.formulacion" 
                                    @change="cambiarFormulacion"
                                    class="form-control-sm form-input"
                                >
                                    <option v-if="administrador" value="m">{{ labels.manual }}</option>
                                    <option v-if="administrador" value="ac">{{ labels.ajusteConsolidacion }}</option>
                                    <option v-if="administrador" value="mc">{{ labels.ajusteCombinado }}</option>
                                    <option value="sf">{{ labels.sumRow }}</option>
                                    <option value="n">{{ labels.niif }}</option>
                                </b-form-select>
                            </div>
                        </div>

                        <!-- Suma Fila -->
                        <div class="col-md-4" v-if="formulacionData?.formulacion === 'sf'">
                            <div class="uniform-form-row mb-3">
                                <label class="field-label">{{ labels.rowToSum }}</label>
                                <input 
                                    v-model="formulacionData.sumFila" 
                                    type="text" 
                                    placeholder="Ej: 1,2,3"
                                    class="form-control form-control-sm form-input"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Selección de NIIFs para Formulación -->
                    <div v-if="formulacionData?.formulacion === 'n'">
                        <div class="form-group mb-3">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <label class="field-label">{{ labels.selectNiifFormulation }}</label>
                                <span class="badge badge-primary">
                                    {{ labels.selected }}: {{ formulacionData.niifFormulacion?.length || 0 }}
                                </span>
                            </div>
                            <div class="search-bar mb-2">
                                <input 
                                    v-model="niifsSearchTerm" 
                                    :placeholder="labels.searchNiifs"
                                    class="form-control form-control-sm"
                                    @input="filtrarNiifs"
                                />
                            </div>
                            <div class="table-container" style="max-height: 300px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px;">
                                <table class="table table-sm table-hover mb-0">
                                    <thead style="position: sticky; top: 0; background: white; z-index: 1;">
                                        <tr>
                                            <th style="width: 40px; text-align: center;">
                                                <input 
                                                    type="checkbox" 
                                                    :checked="selectAllNiifs"
                                                    @change="toggleSelectAllNiifs"
                                                />
                                            </th>
                                            <th style="width: 80px;">NIIF</th>
                                            <th>Descripción</th>
                                            <th style="width: 80px;">Nivel 1</th>
                                            <th>Desc. Nivel 1</th>
                                            <th style="width: 80px;">Nivel 2</th>
                                            <th>Desc. Nivel 2</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="niif in filteredNiifs" :key="niif.niif">
                                            <td style="text-align: center;">
                                                <input 
                                                    type="checkbox" 
                                                    v-model="niif._selected"
                                                    @change="onNiifCheckboxChange(niif)"
                                                />
                                            </td>
                                            <td>{{ niif?.niif }}</td>
                                            <td :title="niif?.nameNiif || niif?.descripcion">
                                                {{ truncarTexto(niif?.nameNiif || niif?.descripcion, 40) }}
                                            </td>
                                            <td>{{ niif?.primer_nivel || '-' }}</td>
                                            <td :title="niif?.namePrimerNivel">
                                                {{ truncarTexto(niif?.namePrimerNivel || '-', 30) }}
                                            </td>
                                            <td>{{ niif?.segundo_nivel || '-' }}</td>
                                            <td :title="niif?.nameSegundoNivel">
                                                {{ truncarTexto(niif?.nameSegundoNivel || '-', 30) }}
                                            </td>
                                        </tr>
                                        <tr v-if="filteredNiifs?.length === 0">
                                            <td colspan="7" class="text-center text-muted py-3">
                                                {{ labels.noNiifs }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="cerrarModalFormulacion">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button 
                        class="reset-btn-primary ml-2" 
                        @click="guardarFormulacion" 
                        :disabled="!isFormulacionValid || isVigenciaBloqueada || isLoading"
                    >
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CONFIRMACIÓN ELIMINAR -->
        <b-modal
            :model-value="showDeleteModal"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="md"
            :title="labels.confirmDelete"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ labels.areYouSure }}</div>
                    <div class="password-policy-subtitle">{{ labels.deleteMessage }}</div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="showDeleteModal = false">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button 
                        class="reset-btn-primary ml-2" 
                        @click="confirmarEliminacion" 
                        :disabled="isVigenciaBloqueada || isLoading"
                        style="background-color: #cd2838; border-color: #cd2838;"
                    >
                        {{ labels.delete }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Balance from '../../services/BalanceNiif'
import PlanCuentas from '../../services/PlanCuentas'
import Company from '../../services/Company'
import Grupo from '../../services/Grupo'
import PlanConsolidacion from '../../services/PlanConsolidacion'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: 'OriConfiguracion',
    components: {
        CorporateDataTable
    },
    props: {
        userId: {
            type: Number,
            default: () => {
                const stored = localStorage.getItem('userId')
                return stored ? parseInt(stored) : null
            }
        }
    },
    data() {
        return {
            administrador: false,
            // Filtros principales
            selReporte: '',
            selReporteAnterior: '',
            selVigencia: '',
            selVigenciaAnterior: '',
            grupoId: 'todos',
            companyId: '',
            companyIdAnterior: '',
            
            // Listas de datos
            listReportes: [],
            listVigencias: [],
            listGrupos: [],
            listCompanies: [],
            filteredCompanies: [],
            companiaUsuario: [],
            
            // Filas y formulaciones
            listFilasPrincipales: [],
            filaSeleccionada: null,
            listFormulaciones: [],
            
            // Estado
            isLoading: false,
            consultado: false,
            showFilters: true,
            showFilas: false,
            
            // Modales
            showModalFila: false,
            showModalFormulacion: false,
            showDeleteModal: false,
            isEditingFila: false,
            isEditingFormulacion: false,
            touchedFila: false,
            
            // Eliminación
            elementoAEliminar: null,
            tipoEliminacion: null,
            
            // Cuentas y NIIFs
            cuentasDisponibles: [],
            filteredCuentas: [],
            niifsDisponibles: [],
            filteredNiifs: [],
            cuentasSearchTerm: '',
            niifsSearchTerm: '',
            selectAllCuentas: false,
            selectAllNiifs: false,
            
            // Formularios
            filaFormData: {
                id: null,
                orden: null,
                descripcion: ''
            },
            formulacionData: {
                id: null,
                orden: null,
                contable: '1',
                actual: '1',
                operacion: '+',
                cuentasSeleccionadas: [],
                formulacion: 'm',
                sumFila: '',
                sumColumna: '',
                celdaFila: '',
                celdaColumna: '',
                niifFormulacion: []
            }
        }
    },
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        
        labels() {
            const es = this.$store.state._language === 'es-ES'
            return {
                title: es ? 'Configuración Reporte ORI' : 'ORI Report Configuration',
                selectReport: es ? 'Seleccione Reporte' : 'Select Report',
                selectValidity: es ? 'Seleccione Vigencia' : 'Select Validity',
                selectCompany: es ? 'Seleccione Compañía' : 'Select Company',
                allGroups: es ? 'Todos los grupos' : 'All groups',
                consultar: es ? 'Consultar' : 'Consult',
                showFilters: es ? 'Mostrar filtros' : 'Show filters',
                hideFilters: es ? 'Ocultar filtros' : 'Hide filters',
                rows: es ? 'Filas Configuradas' : 'Configured Rows',
                rowsConfigured: es ? 'filas configuradas' : 'rows configured',
                notRows: es ? 'No hay filas configuradas' : 'No configured rows',
                addRow: es ? 'Agregar Fila' : 'Add Row',
                editRow: es ? 'Editar Fila' : 'Edit Row',
                createRow: es ? 'Crear Fila' : 'Create Row',
                formulationsFor: es ? 'Formulaciones para' : 'Formulations for',
                formulations: es ? 'Formulaciones' : 'Formulations',
                addFormulation: es ? 'Agregar Formulación' : 'Add Formulation',
                createFormulation: es ? 'Crear Formulación' : 'Create Formulation',
                editFormulation: es ? 'Editar Formulación' : 'Edit Formulation',
                noFormulations: es ? 'No hay formulaciones para esta fila' : 'No formulations for this row',
                loading: es ? 'Cargando información...' : 'Loading information...',
                order: es ? 'Orden' : 'Order',
                description: es ? 'Descripción' : 'Description',
                accounting: es ? 'Contable' : 'Accounting',
                current: es ? 'Actual' : 'Current',
                operation: es ? 'Operación' : 'Operation',
                add: es ? 'Suma' : 'Add',
                subtract: es ? 'Resta' : 'Subtract',
                yes: es ? 'Sí' : 'Yes',
                no: es ? 'No' : 'No',
                selectAccounts: es ? 'Seleccionar Cuentas' : 'Select Accounts',
                searchAccounts: es ? 'Buscar cuentas...' : 'Search accounts...',
                noAccounts: es ? 'No se encontraron cuentas' : 'No accounts found',
                selectNiifFormulation: es ? 'Seleccionar NIIF' : 'Select IFRS',
                searchNiifs: es ? 'Buscar NIIFs...' : 'Search IFRS...',
                noNiifs: es ? 'No se encontraron NIIFs' : 'No IFRS found',
                formulationType: es ? 'Tipo de Formulación' : 'Formulation Type',
                manual: es ? 'Valor Manual' : 'Manual Value',
                ajusteConsolidacion: es ? 'Ajuste de Consolidación' : 'Consolidation Adjustment',
                ajusteCombinado: es ? 'Ajuste Combinado' : 'Combined Adjustment',
                sumRow: es ? 'Suma Fila' : 'Sum Row',
                niif: es ? 'NIIF' : 'IFRS',
                rowToSum: es ? 'Fila(s) a sumar' : 'Row(s) to sum',
                edit: es ? 'Editar' : 'Edit',
                delete: es ? 'Eliminar' : 'Delete',
                save: es ? 'Guardar' : 'Save',
                cancel: es ? 'Cancelar' : 'Cancel',
                confirmDelete: es ? 'Confirmar Eliminación' : 'Confirm Delete',
                areYouSure: es ? '¿Está seguro?' : 'Are you sure?',
                deleteMessage: es ? '¿Está seguro de eliminar este elemento?' : 'Are you sure you want to delete this item?',
                selected: es ? 'Seleccionados' : 'Selected',
                requiredField: es ? 'Este campo es requerido' : 'This field is required',
                detail: es ? 'Detalle' : 'Detail'
            }
        },
        
        isFormValid() {
            return this.selReporte && this.selVigencia && this.companyId
        },
        
        isFormulacionValid() {
            if (!this.formulacionData.contable) return false
            if (!this.formulacionData.actual) return false
            if (!this.formulacionData.operacion) return false
            
            if (this.formulacionData.contable === '1') {
                if (this.formulacionData.cuentasSeleccionadas.length === 0) {
                    return false
                }
            }
            
            if (this.formulacionData.contable === '0' && !this.formulacionData.formulacion) {
                return false
            }
            
            return true
        },
        
        // Data para CorporateDataTable
        formulacionesTableData() {
            return this.listFormulaciones.map(item => ({
                ...item,
                contableText: this.getContable(item?.contable),
                actualText: this.getActual(item?.actual),
                detailText: this.getFormulacionTexto(item)
            }))
        },
        
        formulacionesFields() {
            return [
                { key: 'contableText', label: this.labels.accounting, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'actualText', label: this.labels.current, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'operacion', label: this.labels.operation, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'detailText', label: this.labels.detail, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        formulacionesFilterDefs() {
            return [
                { key: 'contableText', type: 'select', placeholder: 'Contable...', options: ['Sí', 'No'] },
                { key: 'actualText', type: 'select', placeholder: 'Actual...', options: ['Sí', 'No'] },
                { key: 'operacion', type: 'select', placeholder: 'Operación...', options: ['+', '-'] },
                { key: 'detailText', type: 'text', placeholder: 'Buscar detalle...' }
            ]
        },
        
        formulacionesTableLabels() {
            return {
                noRow: this.labels.noFormulations,
                noMatches: this.labels.noFormulations,
                searchPlaceholder: 'Buscar en la tabla...',
                rowsPerPage: 'Filas por página:',
                noSort: 'Sin orden',
                sortAsc: 'A-Z / Menor a mayor',
                sortDesc: 'Z-A / Mayor a menor',
                columns: 'Columnas',
                showAllColumns: 'Mostrar todas',
                filters: 'Filtros',
                clearFilters: 'Limpiar filtros'
            }
        }
    },
    watch: {
        cuentasSearchTerm() {
            this.filtrarCuentas()
        },
        niifsSearchTerm() {
            this.filtrarNiifs()
        },
        selReporte(valNew) {
            if (valNew !== this.selReporteAnterior) {
                this.resetSeleccion()
                this.selReporteAnterior = valNew
            }
        },
        selVigencia(valNew) {
            if (valNew !== this.selVigenciaAnterior) {
                this.resetSeleccion()
                this.selVigenciaAnterior = valNew
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                }
            }
        },
        companyId(valNew) {
            if (valNew !== this.companyIdAnterior) {
                this.resetSeleccion()
                this.companyIdAnterior = valNew
            }
        }
    },
    methods: {
        // Navegación
        goBack() {
            this.$router.back()
        },
        
        toggleFilters() {
            this.showFilters = !this.showFilters
        },
        
        getContable(valContable) {
            return valContable == 1 ? 'Sí' : 'No'
        },
        
        getActual(valActual) {
            return valActual == 1 ? 'Sí' : 'No'
        },
        
        getFormulacionTexto(item) {
            if (!item?.formulacion) return 'Valor Manual'
            
            if (item.formulacion === 'm') {
                return 'Valor Manual'
            }
            if (item.formulacion === 'ac') {
                return 'Ajuste de Consolidación'
            }
            if (item.formulacion === 'mc') {
                return 'Ajuste Combinado'
            }
            if (item.formulacion.startsWith('sf:')) {
                return `Suma Fila(s): ${item.formulacion.substring(3)}`
            }
            if (item.formulacion.startsWith('n:')) {
                return `NIIF: ${item.formulacion.substring(2)}`
            }
            return 'Manual'
        },
        
        truncarTexto(texto, longitud) {
            if (!texto) return '-'
            return texto.length > longitud ? `${texto.substring(0, longitud)}...` : texto
        },
        
        handleChangeReporte() {
            this.resetSeleccion()
        },
        
        handleChangeVigencia() {
            this.resetSeleccion()
        },
        
        handleChangeCompany() {
            this.resetSeleccion()
        },
        
        resetSeleccion() {
            this.showFilas = false
            this.listFilasPrincipales = []
            this.filaSeleccionada = null
            this.listFormulaciones = []
            this.consultado = false
        },
        
        // Método igual al de Niveles - filtra compañías por grupo
        filtrarCompaniesPorGrupo() {
            console.log('=== FILTRO DE COMPAÑÍAS (ORI) ===')
            console.log('Grupo seleccionado (ID):', this.grupoId)
            
            if (this.grupoId && this.grupoId !== 'todos') {
                const grupoIdNum = this.grupoId.toString()
                console.log('Grupo ID convertido:', grupoIdNum)
                
                this.filteredCompanies = this.listCompanies.filter(company => {
                    if (!company?.id_grupos) return false
                    const gruposCompania = company.id_grupos
                        .split(',')
                        .map(id => id.trim())
                    const coincide = gruposCompania.includes(grupoIdNum)
                    return coincide
                })
                console.log('Compañías filtradas:', this.filteredCompanies.map(c => c.nombre))
            } else {
                console.log('Mostrando TODAS las compañías')
                this.filteredCompanies = this.listCompanies
            }
            
            this.companyId = ''
            this.resetSeleccion()
            console.log('=== FIN FILTRO ===')
        },
        
        async consultarFilas() {
            if (!this.isFormValid) {
                this.$toast.warning('Complete todos los campos')
                return
            }
            
            this.isLoading = true
            try {
                const response = await Balance.obtenerFilasPrincipalesOri(
                    this.selReporte,
                    this.companyId,
                    this.selVigencia
                )
                
                let datos = response.data.data || []
                
                // Transformar si viene como objeto anidado
                if (datos.length > 0 && typeof datos[0] === 'object' && !Array.isArray(datos[0])) {
                    const primerElemento = datos[0]
                    const valores = Object.values(primerElemento)
                    datos = valores.filter(item => item && item.id)
                }
                
                if (datos.length === 1 && typeof datos[0] === 'object' && !Array.isArray(datos[0])) {
                    const posiblesFilas = Object.values(datos[0]).filter(item => item && item.id)
                    if (posiblesFilas.length > 0) {
                        datos = posiblesFilas
                    }
                }
                
                // Si no hay filas, intentar copiar solo una vez
                if (datos.length === 0 && !this.isVigenciaBloqueada) {
                    if (!this._intentandoCopiar) {
                        this._intentandoCopiar = true
                        const añoActual = parseInt(this.selVigencia.substring(0, 4))
                        const mes = this.selVigencia.substring(4, 6)
                        const vigenciaAnterior = `${añoActual - 1}${mes}`
                        const userId = this.userId || this.$store.state.userId || localStorage.getItem('userId')
                        
                        try {
                            const responseAnterior = await Balance.obtenerFilasPrincipalesOri(
                                this.selReporte,
                                this.companyId,
                                vigenciaAnterior
                            )
                            
                            let datosAnterior = responseAnterior.data.data || []
                            if (datosAnterior.length > 0 && typeof datosAnterior[0] === 'object' && !Array.isArray(datosAnterior[0])) {
                                const primerElemento = datosAnterior[0]
                                const valores = Object.values(primerElemento)
                                datosAnterior = valores.filter(item => item && item.id)
                            }
                            
                            if (datosAnterior.length > 0) {
                                await Balance.copiarConfiguracionOri(
                                    this.selReporte,
                                    this.companyId,
                                    vigenciaAnterior,
                                    this.selVigencia,
                                    userId
                                )
                                this.$toast.success(`Configuración copiada desde vigencia ${vigenciaAnterior}`)
                                this._intentandoCopiar = false
                                await this.consultarFilas()
                                return
                            } else if (!this.isVigenciaBloqueada) {
                                this.$toast.info(`No hay configuración en la vigencia anterior ${vigenciaAnterior}`)
                            }
                        } catch (error) {
                            console.error('Error al verificar vigencia anterior:', error)
                        }
                        this._intentandoCopiar = false
                    }
                }
                
                this.listFilasPrincipales = datos
                this.showFilas = true
                this.consultado = true
                this.filaSeleccionada = null
                this.listFormulaciones = []
                this.showFilters = false
                
                if (this.listFilasPrincipales.length === 0 && !this.isVigenciaBloqueada) {
                    this.$toast.info('No hay filas configuradas para esta combinación. Puede agregar filas usando el botón +')
                }
            } catch (error) {
                console.error('Error consultando filas:', error)
                this.$toast.error('Error al consultar las filas')
            } finally {
                this.isLoading = false
                delete this._intentandoCopiar
            }
        },
        
        async seleccionarFila(fila) {
            console.log('Fila seleccionada:', fila)
            this.filaSeleccionada = fila
            
            const ordenFila = parseInt(fila.orden)
            if (isNaN(ordenFila)) {
                console.error('Orden de fila inválido:', fila.orden)
                this.$toast.error('Error: Orden de fila inválido')
                return
            }
            
            await this.cargarFormulaciones(ordenFila)
        },
        
        async cargarFormulaciones(ordenFila) {
            console.log('Cargando formulaciones para orden_fila:', ordenFila)
            if (!ordenFila || isNaN(ordenFila)) {
                console.error('orden_fila inválido:', ordenFila)
                this.$toast.error('Error: Orden de fila inválido')
                return
            }
            
            this.isLoading = true
            try {
                const response = await Balance.obtenerFormulacionesOri(
                    this.selReporte,
                    this.companyId,
                    this.selVigencia,
                    ordenFila
                )
                this.listFormulaciones = response.data.data || []
                console.log('Formulaciones cargadas:', this.listFormulaciones.length)
            } catch (error) {
                console.error('Error cargando formulaciones:', error)
                this.listFormulaciones = []
                this.$toast.error('Error al cargar las formulaciones')
            } finally {
                this.isLoading = false
            }
        },
        
        // ---- MODAL FILA ----
        abrirModalCrearFila() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            this.isEditingFila = false
            this.touchedFila = false
            this.filaFormData = {
                id: null,
                orden: null,
                descripcion: ''
            }
            this.showModalFila = true
        },
        
        editarFila(fila) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            this.isEditingFila = true
            this.touchedFila = false
            this.filaFormData = {
                id: fila.id,
                orden: fila.orden,
                descripcion: fila.descripcion || ''
            }
            this.showModalFila = true
        },
        
        async guardarFila() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            
            if (!this.filaFormData.orden) {
                this.touchedFila = true
                this.$toast.warning('El orden es obligatorio')
                return
            }
            
            const userId = this.userId || this.$store.state.userId || localStorage.getItem('userId')
            if (!userId) {
                this.$toast.error('No se encontró el ID de usuario')
                return
            }
            
            this.isLoading = true
            try {
                const payload = {
                    accion: this.isEditingFila ? 'UPDATE_FILA_PRINCIPAL' : 'INSERT_FILA_PRINCIPAL',
                    id: this.filaFormData.id || null,
                    codReporte: parseInt(this.selReporte),
                    vigencia: this.selVigencia,
                    id_compania: parseInt(this.companyId),
                    orden: parseInt(this.filaFormData.orden),
                    descripcion: this.filaFormData.descripcion || null,
                    u_crea: parseInt(userId),
                    u_modifica: parseInt(userId)
                }
                
                await Balance.gestionarFilasPrincipalesOri(payload)
                this.$toast.success(this.isEditingFila ? 'Fila actualizada' : 'Fila creada')
                this.cerrarModalFila()
                await this.consultarFilas()
            } catch (error) {
                console.error('Error guardando fila:', error)
                this.$toast.error(error.response?.data?.message || 'Error al guardar')
            } finally {
                this.isLoading = false
            }
        },
        
        cerrarModalFila() {
            this.showModalFila = false
            this.filaFormData = {
                id: null,
                orden: null,
                descripcion: ''
            }
            this.touchedFila = false
        },
        
        // ---- MODAL FORMULACIÓN ----
        async abrirModalCrearFormulacion() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            
            if (!this.filaSeleccionada) {
                this.$toast.warning('Seleccione una fila primero')
                return
            }
            
            this.isEditingFormulacion = false
            await this.cargarCuentasYNiifs()
            
            this.formulacionData = {
                id: null,
                orden: this.filaSeleccionada.orden,
                contable: '1',
                actual: '1',
                operacion: '+',
                cuentasSeleccionadas: [],
                formulacion: 'm',
                sumFila: '',
                sumColumna: '',
                celdaFila: '',
                celdaColumna: '',
                niifFormulacion: []
            }
            
            this.selectAllCuentas = false
            this.cuentasSearchTerm = ''
            this.filtrarCuentas()
            this.showModalFormulacion = true
        },
        
        async editarFormulacion(formulacion) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            
            const formulacionClone = JSON.parse(JSON.stringify(formulacion))
            this.isEditingFormulacion = true
            await this.cargarCuentasYNiifs()
            
            if (formulacionClone.contable == 1) {
                const cuentasSeleccionadasArray = formulacionClone.cuentas ? formulacionClone.cuentas.split(',') : []
                
                this.cuentasDisponibles.forEach(cuenta => {
                    cuenta._selected = cuentasSeleccionadasArray.includes(cuenta.cuenta)
                })
                
                this.formulacionData = {
                    id: formulacionClone.id,
                    orden: this.filaSeleccionada.orden,
                    contable: '1',
                    actual: formulacionClone.actual !== undefined && formulacionClone.actual !== null
                        ? formulacionClone.actual.toString()
                        : '1',
                    operacion: formulacionClone.operacion || '+',
                    cuentasSeleccionadas: cuentasSeleccionadasArray,
                    formulacion: 'm',
                    sumFila: '',
                    sumColumna: '',
                    celdaFila: '',
                    celdaColumna: '',
                    niifFormulacion: []
                }
            } else {
                let formulacionTipo = 'm'
                let sumFila = ''
                let niifFormulacionArray = []
                
                if (formulacionClone.formulacion) {
                    if (formulacionClone.formulacion.startsWith('sf:')) {
                        formulacionTipo = 'sf'
                        sumFila = formulacionClone.formulacion.substring(3)
                    } else if (formulacionClone.formulacion.startsWith('n:')) {
                        formulacionTipo = 'n'
                        niifFormulacionArray = formulacionClone.formulacion.substring(2).split(',')
                            .map(n => n.trim())
                            .filter(n => n !== '')
                        
                        await this.$nextTick()
                        this.niifsDisponibles.forEach(niif => {
                            niif._selected = niifFormulacionArray.includes(niif.niif.toString())
                        })
                    } else if (formulacionClone.formulacion === 'ac') {
                        formulacionTipo = 'ac'
                    } else if (formulacionClone.formulacion === 'mc') {
                        formulacionTipo = 'mc'
                    } else if (formulacionClone.formulacion === 'm') {
                        formulacionTipo = 'm'
                    }
                }
                
                this.formulacionData = {
                    id: formulacionClone.id,
                    orden: this.filaSeleccionada.orden,
                    contable: '0',
                    actual: formulacionClone.actual !== undefined && formulacionClone.actual !== null
                        ? formulacionClone.actual.toString()
                        : '1',
                    operacion: formulacionClone.operacion || '+',
                    cuentasSeleccionadas: [],
                    formulacion: formulacionTipo,
                    sumFila: sumFila,
                    sumColumna: '',
                    celdaFila: '',
                    celdaColumna: '',
                    niifFormulacion: niifFormulacionArray
                }
            }
            
            this.filtrarCuentas()
            this.filtrarNiifs()
            this.updateSelectAllCuentas()
            this.updateSelectAllNiifs()
            this.showModalFormulacion = true
        },
        
        async guardarFormulacion() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar')
                return
            }
            
            if (!this.isFormulacionValid) {
                this.$toast.warning('Complete los campos requeridos')
                return
            }
            
            const userId = this.userId || this.$store.state.userId || localStorage.getItem('userId')
            if (!userId) {
                this.$toast.error('No se encontró el ID de usuario')
                return
            }
            
            this.isLoading = true
            try {
                let cuentasFinal = null
                let formulacionFinal = ''
                
                if (this.formulacionData.contable === '1') {
                    cuentasFinal = this.formulacionData.cuentasSeleccionadas.join(',')
                    formulacionFinal = ''
                } else {
                    if (this.formulacionData.formulacion === 'sf') {
                        formulacionFinal = `sf:${this.formulacionData.sumFila}`
                    } else if (this.formulacionData.formulacion === 'n') {
                        formulacionFinal = `n:${this.formulacionData.niifFormulacion.join(',')}`
                    } else if (this.formulacionData.formulacion === 'm') {
                        formulacionFinal = 'm'
                    } else if (this.formulacionData.formulacion === 'ac') {
                        formulacionFinal = 'ac'
                    } else if (this.formulacionData.formulacion === 'mc') {
                        formulacionFinal = 'mc'
                    }
                }
                
                const payload = {
                    accion: this.isEditingFormulacion ? 'UPDATE_FORMULACION' : 'INSERT_FORMULACION',
                    id: this.formulacionData.id || null,
                    codReporte: parseInt(this.selReporte),
                    vigencia: this.selVigencia,
                    id_compania: parseInt(this.companyId),
                    orden: parseInt(this.formulacionData.orden),
                    reporte: 'ORI',
                    niif: null,
                    cuentas: cuentasFinal,
                    contable: parseInt(this.formulacionData.contable),
                    actual: parseInt(this.formulacionData.actual),
                    operacion: this.formulacionData.operacion,
                    formulacion: formulacionFinal,
                    valor: 0,
                    u_crea: parseInt(userId),
                    u_modifica: parseInt(userId)
                }
                
                console.log('Payload enviado:', payload)
                await Balance.gestionarFormulacionesOri(payload)
                this.$toast.success(this.isEditingFormulacion ? 'Formulación actualizada' : 'Formulación creada')
                this.cerrarModalFormulacion()
                await this.cargarFormulaciones(this.filaSeleccionada.orden)
            } catch (error) {
                console.error('Error guardando formulación:', error)
                this.$toast.error(error.response?.data?.message || 'Error al guardar')
            } finally {
                this.isLoading = false
            }
        },
        
        cerrarModalFormulacion() {
            this.showModalFormulacion = false
            this.formulacionData = {
                id: null,
                orden: null,
                contable: '1',
                actual: '1',
                operacion: '+',
                cuentasSeleccionadas: [],
                formulacion: 'm',
                sumFila: '',
                sumColumna: '',
                celdaFila: '',
                celdaColumna: '',
                niifFormulacion: []
            }
            this.selectAllCuentas = false
            this.selectAllNiifs = false
            this.cuentasSearchTerm = ''
            this.niifsSearchTerm = ''
        },
        
        // ---- ELIMINACIÓN ----
        eliminarFila(fila) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar')
                return
            }
            this.elementoAEliminar = fila
            this.tipoEliminacion = 'fila'
            this.showDeleteModal = true
        },
        
        eliminarFormulacion(formulacion) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar')
                return
            }
            this.elementoAEliminar = formulacion
            this.tipoEliminacion = 'formulacion'
            this.showDeleteModal = true
        },
        
        async confirmarEliminacion() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar')
                return
            }
            
            if (!this.elementoAEliminar) return
            
            this.isLoading = true
            try {
                if (this.tipoEliminacion === 'fila') {
                    await Balance.eliminarFilaPrincipalOri(this.elementoAEliminar.id)
                    this.$toast.success('Fila eliminada')
                    await this.consultarFilas()
                } else {
                    await Balance.eliminarFormulacionOri(this.elementoAEliminar.id)
                    this.$toast.success('Formulación eliminada')
                    await this.cargarFormulaciones(this.filaSeleccionada.orden)
                }
                this.showDeleteModal = false
                this.elementoAEliminar = null
                this.tipoEliminacion = null
            } catch (error) {
                console.error('Error eliminando:', error)
                this.$toast.error('Error al eliminar')
            } finally {
                this.isLoading = false
            }
        },
        
        // ---- CUENTAS Y NIIFs ----
        async cargarCuentasYNiifs() {
            try {
                const [cuentasRes, niifsRes] = await Promise.all([
                    PlanCuentas.planCuentPorCompaVig(this.companyId, this.selVigencia),
                    PlanConsolidacion.getPlanConsPorCompVige(this.selVigencia, this.selReporte, this.companyId)
                ])
                
                this.cuentasDisponibles = cuentasRes.data.planCuentas || []
                this.cuentasDisponibles.forEach(c => {
                    c._selected = false
                })
                this.filteredCuentas = [...this.cuentasDisponibles]
                
                this.niifsDisponibles = (niifsRes.data.planConsNiif || []).map(n => {
                    return {
                        ...n,
                        _selected: false,
                        niif: n.niif ? n.niif.toString() : n.niif
                    }
                })
                this.filteredNiifs = [...this.niifsDisponibles]
            } catch (error) {
                console.error('Error cargando cuentas/NIIFs:', error)
            }
        },
        
        cambiarTipoFormulacion() {
            if (this.formulacionData.contable === '1') {
                this.formulacionData.cuentasSeleccionadas = []
                this.formulacionData.formulacion = 'm'
                this.formulacionData.sumFila = ''
                this.formulacionData.niifFormulacion = []
                this.selectAllCuentas = false
            } else {
                this.formulacionData.cuentasSeleccionadas = []
            }
        },
        
        cambiarFormulacion() {
            this.formulacionData.sumFila = ''
            this.formulacionData.niifFormulacion = []
        },
        
        filtrarCuentas() {
            if (!this.cuentasSearchTerm) {
                this.filteredCuentas = [...this.cuentasDisponibles]
            } else {
                const term = this.cuentasSearchTerm.toLowerCase()
                this.filteredCuentas = this.cuentasDisponibles.filter(cuenta =>
                    (cuenta.cuenta && cuenta.cuenta.toString().toLowerCase().includes(term)) ||
                    (cuenta.descripcion && cuenta.descripcion.toLowerCase().includes(term))
                )
            }
            
            this.filteredCuentas.forEach(cuenta => {
                cuenta._selected = this.formulacionData.cuentasSeleccionadas.includes(cuenta.cuenta)
            })
            this.updateSelectAllCuentas()
        },
        
        filtrarNiifs() {
            if (!this.niifsSearchTerm) {
                this.filteredNiifs = [...this.niifsDisponibles]
            } else {
                const term = this.niifsSearchTerm.toLowerCase()
                this.filteredNiifs = this.niifsDisponibles.filter(niif =>
                    (niif.niif && niif.niif.toString().toLowerCase().includes(term)) ||
                    ((niif.nameNiif || niif.descripcion) && (niif.nameNiif || niif.descripcion).toLowerCase().includes(term)) ||
                    (niif.primer_nivel && niif.primer_nivel.toString().toLowerCase().includes(term)) ||
                    (niif.segundo_nivel && niif.segundo_nivel.toString().toLowerCase().includes(term)) ||
                    (niif.namePrimerNivel && niif.namePrimerNivel.toLowerCase().includes(term)) ||
                    (niif.nameSegundoNivel && niif.nameSegundoNivel.toLowerCase().includes(term))
                )
            }
            
            this.filteredNiifs.forEach(niif => {
                niif._selected = this.formulacionData.niifFormulacion.includes(niif.niif.toString())
            })
            this.updateSelectAllNiifs()
        },
        
        onCuentaCheckboxChange(cuenta) {
            if (cuenta._selected) {
                if (!this.formulacionData.cuentasSeleccionadas.includes(cuenta.cuenta)) {
                    this.formulacionData.cuentasSeleccionadas.push(cuenta.cuenta)
                }
            } else {
                const index = this.formulacionData.cuentasSeleccionadas.indexOf(cuenta.cuenta)
                if (index > -1) {
                    this.formulacionData.cuentasSeleccionadas.splice(index, 1)
                }
            }
            this.updateSelectAllCuentas()
        },
        
        onNiifCheckboxChange(niif) {
            const niifString = niif.niif.toString()
            if (niif._selected) {
                if (!this.formulacionData.niifFormulacion.includes(niifString)) {
                    this.formulacionData.niifFormulacion.push(niifString)
                }
            } else {
                const index = this.formulacionData.niifFormulacion.indexOf(niifString)
                if (index > -1) {
                    this.formulacionData.niifFormulacion.splice(index, 1)
                }
            }
            this.updateSelectAllNiifs()
        },
        
        updateSelectAllCuentas() {
            if (this.filteredCuentas.length === 0) {
                this.selectAllCuentas = false
                return
            }
            this.selectAllCuentas = this.filteredCuentas.every(cuenta =>
                this.formulacionData.cuentasSeleccionadas.includes(cuenta.cuenta)
            )
        },
        
        updateSelectAllNiifs() {
            if (this.filteredNiifs.length === 0) {
                this.selectAllNiifs = false
                return
            }
            this.selectAllNiifs = this.filteredNiifs.every(niif =>
                this.formulacionData.niifFormulacion.includes(niif.niif.toString())
            )
        },
        
        toggleSelectAllCuentas() {
            if (this.selectAllCuentas) {
                this.filteredCuentas.forEach(cuenta => {
                    const index = this.formulacionData.cuentasSeleccionadas.indexOf(cuenta.cuenta)
                    if (index > -1) {
                        this.formulacionData.cuentasSeleccionadas.splice(index, 1)
                    }
                    cuenta._selected = false
                })
                this.selectAllCuentas = false
            } else {
                this.filteredCuentas.forEach(cuenta => {
                    if (!this.formulacionData.cuentasSeleccionadas.includes(cuenta.cuenta)) {
                        this.formulacionData.cuentasSeleccionadas.push(cuenta.cuenta)
                    }
                    cuenta._selected = true
                })
                this.selectAllCuentas = true
            }
        },
        
        toggleSelectAllNiifs() {
            if (this.selectAllNiifs) {
                this.filteredNiifs.forEach(niif => {
                    const niifString = niif.niif.toString()
                    const index = this.formulacionData.niifFormulacion.indexOf(niifString)
                    if (index > -1) {
                        this.formulacionData.niifFormulacion.splice(index, 1)
                    }
                    niif._selected = false
                })
                this.selectAllNiifs = false
            } else {
                this.filteredNiifs.forEach(niif => {
                    const niifString = niif.niif.toString()
                    if (!this.formulacionData.niifFormulacion.includes(niifString)) {
                        this.formulacionData.niifFormulacion.push(niifString)
                    }
                    niif._selected = true
                })
                this.selectAllNiifs = true
            }
        },
        
        // ---- DATOS INICIALES ----
        async cargarDatosIniciales() {
            this.isLoading = true
            try {
                const [reportesRes, vigenciasRes, gruposRes] = await Promise.all([
                    Balance.getReportes(),
                    PlanCuentas.getVigenciaPlancuentas(),
                    Grupo.getAllGrupos()
                ])
                
                this.listReportes = reportesRes.data.reportes || []
                this.listVigencias = vigenciasRes.data.getVigencia || []
                this.listGrupos = gruposRes.data.grupos || []
                
                await this.cargarCompaniesUsuario()
            } catch (error) {
                console.error('Error cargando datos iniciales:', error)
                this.$toast.error('Error al cargar datos iniciales')
            } finally {
                this.isLoading = false
            }
        },
        
        async cargarCompaniesUsuario() {
            try {
                const userResult = await Balance.leaCompaniaUsuario()
                this.companiaUsuario = userResult.data.companiaUsuario || []
                
                const companiesResult = await Company.getAllCompanies()
                const allCompanies = companiesResult.data.companies || []
                
                const userRole = this.$store.state.userRole || localStorage.getItem('userRole')
                const userId = this.userId || this.$store.state.userId || localStorage.getItem('userId')
                
                const filtered = allCompanies.filter(company => {
                    if (userRole == 1) return true
                    return this.companiaUsuario.some(
                        uc => uc.id_usuario == userId && uc.id_compania == company.id
                    )
                })
                
                this.listCompanies = filtered
                this.filteredCompanies = [...filtered]
            } catch (error) {
                console.error('Error cargando compañías:', error)
                this.listCompanies = []
                this.filteredCompanies = []
            }
        }
    },
    mounted() {
        this.cargarDatosIniciales()
        if (this.$route.name == "oriReporteADM") {
            this.administrador = true
        }
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 180px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.toggle-filters-btn {
    color: #2e507a;
    font-weight: 500;
    font-size: 14px;
    padding: 4px 12px;
    text-decoration: none;
    border: 1px solid #dee2e6;
    border-radius: 7px;
    background: white;
    height: 38px;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.toggle-filters-btn:hover {
    background: #f8f9fa;
    text-decoration: none;
    color: #1a3455;
}

.admin-table-block {
    background: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.empty-table-icon {
    font-size: 40px;
    color: #d0d0d0;
}

/* Estilos para items de fila */
.fila-item {
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #f8f9fa;
}

.fila-item:hover {
    background-color: #e9ecef;
    border-color: #2e507a !important;
}

.fila-item.selected-item {
    background-color: #e8f0fe;
    border-color: #2e507a !important;
    border-width: 2px !important;
}

.md-fab {
    border: none;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.md-fab:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.05);
}

.md-fab:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.reset-btn-danger {
    background-color: #cd2838;
    border-color: #cd2838;
    color: white;
}

.reset-btn-danger:hover:not(:disabled) {
    background-color: #b0202e;
    border-color: #a01d2a;
    color: white;
}

.reset-btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Estilos para modales */
.uniform-modal-body { 
    padding: 0 10px; 
}

.uniform-form-row {
    margin-bottom: 12px;
}

.uniform-label-col {
    padding-right: 10px;
}

.field-label {
    margin-bottom: 0;
    font-weight: 500;
    font-size: 14px;
}

.field-label.required::after {
    content: '*';
    color: #dc3545;
    margin-left: 4px;
}

.form-input {
    width: 100%;
    height: 38px;
    font-size: 14px;
}

.reset-modal-header {
    background-color: #2e507a;
    color: white;
    padding: 16px 20px;
    border-radius: 8px 8px 0 0;
}

.reset-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.reset-modal-body {
    padding: 24px 20px;
    background-color: #f8f9fa;
}

.password-policy-box {
    background: white;
    border-radius: 8px;
    padding: 16px 20px;
    border-left: 4px solid #dc3545;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.password-policy-title {
    font-weight: 600;
    font-size: 15px;
    color: #dc3545;
    margin-bottom: 4px;
}

.password-policy-subtitle {
    font-size: 13px;
    color: #6c757d;
}

.reset-dialog-actions {
    padding-top: 8px;
}

.reset-btn-primary {
    background-color: #2e507a;
    border-color: #2e507a;
    color: white;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}

.reset-btn-primary:hover:not(:disabled) {
    background-color: #1e3a5f;
    border-color: #1a3455;
    color: white;
}

.reset-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.reset-btn-secondary {
    background-color: #e9ecef;
    border-color: #ced4da;
    color: #495057;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}

.reset-btn-secondary:hover {
    background-color: #dde1e6;
    border-color: #b8c1ca;
}

/* Estilos para tablas dentro de modales */
.table-container {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: hidden;
}

.table-container table {
    margin-bottom: 0;
}

.table-container th {
    background-color: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 1;
}

/* Responsive */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .toggle-filters-btn {
        font-size: 12px;
        height: 36px;
        padding: 4px 8px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .niif-header .filters-container {
        gap: 4px;
    }
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
    .toggle-filters-btn {
        width: 100%;
        justify-content: center;
        height: 36px;
        margin-bottom: 4px;
        margin-right: 0 !important;
    }
    .admin-table-block {
        padding: 8px;
    }
    .uniform-label-col {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 4px;
        text-align: left !important;
    }
    .uniform-form-row .col-md-8 {
        flex: 0 0 100%;
        max-width: 100%;
    }
}
</style>